import React, { useState, useRef, useCallback, useEffect } from 'react'

import { Button, Icon, Payment } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, shape, string } from 'prop-types'

import PaymentForm from 'Pods/MySubscriptions/PaymentForm'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import useSentry from 'Pods/Shared/Hooks/useSentry'
import I18n from 'Pods/Shared/I18n'

import { NOOP } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import {
  CARD_FORM_STYLES,
  CARD_FORM_PARAMS,
  GOOGLE_PAY_BUTTON_PARAMS,
  APPLE_PAY_BUTTON_PARAMS,
  PAYPAL_BUTTON_PARAMS,
} from './paymentFormConfig'

const RETRY_CARD_FORM_KEY = 'payment_retry_card_form'
const CHECKOUT_SUCCESS_REDIRECT_DELAY_MS = 5000
const UPDATE_MODE_SUCCESS_REDIRECT_DELAY_MS = 2000
const WALLET_METHODS = ['googlePay', 'applePay', 'paypal']

const EMPTY_TRACKING = {
  trackPayClick: NOOP,
  trackSuccess: NOOP,
  trackPaymentMethodSelection: NOOP,
  trackFailure: NOOP,
}

function toMessage(value) {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number') return String(value)
  return ''
}

function toDetailsMessage(value) {
  if (typeof value === 'string') return value.trim()

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return Object.values(value).map(toMessage).filter(Boolean).join(', ')
  }

  return ''
}

function sanitizeNotificationMessage(message) {
  return message
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/`/g, '&#96;')
}

function SolidgatePaymentSection({
  merchant,
  signature,
  paymentIntent,
  returnPath = '/profile/settings#tab-settings-subscription',
  locale = 'en',
  onFormReady = NOOP,
  onFormValidityChange = NOOP,
  onPaymentProcessing = NOOP,
  onPaymentSuccess = NOOP,
  onRetry = NOOP,
  isUpdateMode = false,
  isPaymentBlocked = false,
  isPaymentUpdating = false,
  translationKeyPrefix = 'subscription_checkout',
  tracking = EMPTY_TRACKING,
}) {
  const [isCardFormVisible, setIsCardFormVisible] = useState(false)
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false)
  const [hasPaymentFailed, setHasPaymentFailed] = useState(false)
  const breakpoint = useBreakpoint()
  const isSmallMobile = breakpoint === 'xs'

  const isRetryingRef = useRef(false)
  const googlePayRef = useRef(null)
  const applePayRef = useRef(null)
  const paypalRef = useRef(null)
  // Keep redirect timer handle to avoid stale redirects on repeated success or unmount.
  const updateModeRedirectTimerRef = useRef(null)

  const { captureException } = useSentry()
  const { showSuccess, showError } = useNotifications()
  const {
    trackPayClick,
    trackSuccess,
    trackPaymentMethodSelection,
    trackFailure,
  } = tracking

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  const translationKey = useCallback(
    (suffix) => `${translationKeyPrefix}.${suffix}`,
    [translationKeyPrefix],
  )

  const redirect = useCallback(() => {
    window.location.href = returnPath
  }, [returnPath])

  useEffect(() => {
    const shouldOpen = sessionStorage.getItem(RETRY_CARD_FORM_KEY)
    if (shouldOpen) {
      sessionStorage.removeItem(RETRY_CARD_FORM_KEY)
      setIsCardFormVisible(true)
    }
  }, [])

  useEffect(() => {
    const cleanupRetryFlag = () => {
      if (!isRetryingRef.current) {
        sessionStorage.removeItem(RETRY_CARD_FORM_KEY)
      }
    }

    window.addEventListener('beforeunload', cleanupRetryFlag)
    return () => window.removeEventListener('beforeunload', cleanupRetryFlag)
  }, [])

  useEffect(() => {
    if (!isPaymentSuccessful) return undefined

    const timer = setTimeout(redirect, CHECKOUT_SUCCESS_REDIRECT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isPaymentSuccessful, redirect])

  useEffect(() => () => {
    if (updateModeRedirectTimerRef.current) {
      clearTimeout(updateModeRedirectTimerRef.current)
    }
  }, [])

  const getSuccessMessage = useCallback((event) => {
    const message = toMessage(event?.message) || translate(translationKey('payment_success_toast'))
    return sanitizeNotificationMessage(message)
  }, [translate, translationKey])

  const getFailMessage = useCallback((event) => {
    const message = toMessage(event?.message)
      || toMessage(event?.error?.message)
      || translate(translationKey('payment_error_toast'))
    return sanitizeNotificationMessage(message)
  }, [translate, translationKey])

  const getErrorMessage = useCallback((event) => {
    const message = toMessage(event?.value?.message)
      || toDetailsMessage(event?.details?.message)
      || translate(translationKey('payment_error_toast'))
    return sanitizeNotificationMessage(message)
  }, [translate, translationKey])

  const handleSuccess = (event) => {
    const successMessage = getSuccessMessage(event)

    showSuccess(successMessage)
    trackSuccess()

    if (!isUpdateMode) {
      onPaymentProcessing()

      // Parent-driven flow (e.g. guest checkout → create-account step): the
      // caller owns navigation, so we skip our auto-redirect to avoid racing it.
      if (onPaymentSuccess !== NOOP) {
        onPaymentSuccess(event)
        return
      }

      setIsPaymentSuccessful(true)
      return
    }

    if (updateModeRedirectTimerRef.current) {
      clearTimeout(updateModeRedirectTimerRef.current)
    }

    updateModeRedirectTimerRef.current = setTimeout(
      redirect,
      UPDATE_MODE_SUCCESS_REDIRECT_DELAY_MS,
    )
  }

  const handleFail = (event) => {
    const errorDetail = getFailMessage(event)

    showError(errorDetail)
    captureException(new Error(`Payment failed: ${errorDetail}`))
    trackFailure(errorDetail)
    setHasPaymentFailed(true)
    // The SDK keeps reporting the card as valid while it shows the decline screen, where submit() is a no-op.
    onFormValidityChange(false)
  }

  const handleRetry = () => {
    isRetryingRef.current = true
    if (isCardFormVisible) {
      sessionStorage.setItem(RETRY_CARD_FORM_KEY, 'true')
    }
    onRetry()
    window.location.reload()
  }

  const handleError = (event) => {
    const errorDetail = getErrorMessage(event)

    showError(errorDetail)
    captureException(new Error(`Form error: ${errorDetail}`))
  }

  const handleSubmit = () => {
    trackPayClick()
  }

  const handleFormReadyInternal = (form) => {
    onFormReady(form)
  }

  const handleToggleCardForm = () => {
    if (!isCardFormVisible) {
      trackPaymentMethodSelection('card')
    }
    setIsCardFormVisible((prev) => !prev)
  }

  const handleInteraction = (event) => {
    if (event?.cardForm && onFormValidityChange) {
      onFormValidityChange(event.cardForm.isValid)
    }

    const { target } = event || {}
    if (target?.interaction === 'click' && WALLET_METHODS.includes(target.name)) {
      trackPaymentMethodSelection(target.name)
    }
  }

  const cardFormClassName = isCardFormVisible
    ? 'payment-section__card-form'
    : 'payment-section__card-form payment-section__card-form--hidden'

  // The SDK keeps wallet buttons clickable while an update is pending or after it failed, so they would
  // charge the stale intent. `inert` also blocks keyboard activation, which pointer-events alone does not.
  const walletProps = (element) => (isPaymentBlocked || isPaymentUpdating
    ? { className: `payment-section__${element} payment-section__${element}--disabled`, inert: '' }
    : { className: `payment-section__${element}` })

  if (isPaymentSuccessful) {
    return (
      <div className="payment-section payment-section--success">
        <p className="cgt-text-medium cgt-legacy-color-grey-50">
          {translate(translationKey('payment_success_processing'))}
        </p>
      </div>
    )
  }

  return (
    <div className="payment-section">
      <div ref={googlePayRef} {...walletProps('google-pay')} />
      <div ref={applePayRef} {...walletProps('apple-pay')} />
      <div ref={paypalRef} {...walletProps('paypal')} />

      {!isPaymentBlocked && !hasPaymentFailed && (
        <>
          <div className="payment-section__divider">
            <span className="cgt-text-medium cgt-legacy-color-grey-50">
              {translate(translationKey('divider_text'))}
            </span>
          </div>

          <Button
            type="secondary"
            size={isSmallMobile ? 'small' : 'medium'}
            onClick={handleToggleCardForm}
            expand
          >
            <div className="payment-section__card-button-content">
              <Icon svgComponent={Payment} />
              {translate(translationKey(isUpdateMode ? 'update_credit_card_button' : 'credit_card_button'))}
            </div>
          </Button>
        </>
      )}

      <div className={cardFormClassName}>
        <PaymentForm
          merchant={merchant}
          signature={signature}
          paymentIntent={paymentIntent}
          styles={CARD_FORM_STYLES}
          formParams={CARD_FORM_PARAMS}
          googlePayButtonParams={GOOGLE_PAY_BUTTON_PARAMS}
          applePayButtonParams={APPLE_PAY_BUTTON_PARAMS}
          paypalButtonParams={PAYPAL_BUTTON_PARAMS}
          googlePayContainerRef={googlePayRef}
          applePayContainerRef={applePayRef}
          paypalContainerRef={paypalRef}
          onSuccess={handleSuccess}
          onFail={handleFail}
          onError={handleError}
          onReadyPaymentInstance={handleFormReadyInternal}
          onSubmit={handleSubmit}
          onInteraction={handleInteraction}
        />
      </div>

      {(hasPaymentFailed || isPaymentBlocked) && (
        <Button type="secondary" size="small" onClick={handleRetry} expand>
          {translate(translationKey('payment_retry_button'))}
        </Button>
      )}
    </div>
  )
}

SolidgatePaymentSection.propTypes = {
  merchant: string.isRequired,
  signature: string.isRequired,
  paymentIntent: string.isRequired,
  returnPath: string,
  locale: string,
  onFormReady: func,
  onFormValidityChange: func,
  onPaymentProcessing: func,
  onPaymentSuccess: func,
  onRetry: func,
  isUpdateMode: bool,
  isPaymentBlocked: bool,
  isPaymentUpdating: bool,
  translationKeyPrefix: string,
  tracking: shape({
    trackPayClick: func,
    trackSuccess: func,
    trackPaymentMethodSelection: func,
    trackFailure: func,
  }),
}

export default SolidgatePaymentSection
